import moment from 'moment'
import { clearBasket, setBasket, stopLoading } from 'Rentlio/state/actions/ui'
import { getRoomRequests, getReferenceCode } from 'Rentlio/state/selectors/payment'
import {
  BOOK_RESERVATION,
  BOOK_RESERVATION_FAILED,
  CANCEL_RESERVATION,
  FETCH_AVAILABILITY,
  FETCH_AVAILABILITY_FOR_MONTH,
  FETCH_RESERVATION,
  FETCH_MONRI_CLIENT_SECRET,
  FETCH_STRIPE_CLIENT_SECRET,
  FETCH_M2PAY_CLIENT_SECRET,
  ACCEPT_BOOKING,
  SET_REQUEST,
  UPDATE_GUEST_REGISTRATION,
  SAFERPAY_ALIAS_INSERT,
  SAFERPAY_PAYMENT_PAGE_INITIALIZE,
  SAFERPAY_TRANSACTION_INITIALIZE
} from 'Rentlio/state/types'
import { DATE_URL_FORMAT } from 'Rentlio/utils/dateFormatEnum'
import rentlioLocalStorage from 'Rentlio/helper/rentlioLocalStorage'
import { logErrorWithJsonData } from 'Rentlio/utils/logging'

const PAYMENT_PAGE_BOOKING_STORAGE_KEY = 'payment-page-booking'

export const fetchAvailability = data => (dispatch, getState) => {
  const { request } = getState()
  const from = moment.unix(request.from).format(DATE_URL_FORMAT)
  const to = moment.unix(request.to).format(DATE_URL_FORMAT)

  dispatch({
    type: FETCH_AVAILABILITY,
    meta: {
      async: true,
      path: '/availability',
      method: 'GET',
      body: data
    }
  }).finally(() => {
    setTimeout(() => dispatch(stopLoading()), 500)
  })

  // Dont reset basket if dates havent changed
  if (data.from === from && data.to === to) {
    return
  }

  dispatch(clearBasket())
}

export const fetchAvailabilityForMonth = (unitTypeUuid, year, month) => ({
  type: FETCH_AVAILABILITY_FOR_MONTH,
  meta: {
    async: true,
    path: `/availability/${unitTypeUuid}/${year}/${month}`,
    method: 'GET',
    body: {}
  }
})

export const bookReservation = (guest, payment) => (dispatch, getState) =>
  dispatch({
    type: BOOK_RESERVATION,
    meta: {
      async: true,
      path: '/reservation',
      method: 'POST',
      body: {
        guest,
        payment,
        rooms: getRoomRequests(getState()),
        referenceCode: getReferenceCode(getState())
      }
    }
  })

export const savePaymentPageBooking = (guest, payment) => (dispatch, getState) => {
  const state = getState()

  rentlioLocalStorage.add(PAYMENT_PAGE_BOOKING_STORAGE_KEY, {
    body: {
      guest,
      payment,
      rooms: getRoomRequests(state),
      referenceCode: getReferenceCode(state)
    },
    basket: state.basket,
    request: state.request
  })
}

// Books the reservation snapshotted before the payment page redirect. The snapshot is
// removed up front so a refresh can never send the booking twice.
export const completePaymentPageBooking = () => dispatch => {
  const snapshot = rentlioLocalStorage.get(PAYMENT_PAGE_BOOKING_STORAGE_KEY)
  rentlioLocalStorage.remove(PAYMENT_PAGE_BOOKING_STORAGE_KEY)

  if (!snapshot?.body) {
    // the payment was already authorized at SaferPay - this must be visible for support
    logErrorWithJsonData(
      'Payment page return without stored booking data',
      {},
      { critical: 'true', description: 'Reservation cannot be created after an authorized payment page transaction' }
    )
    dispatch({
      type: BOOK_RESERVATION_FAILED,
      payload: { message: 'Missing stored booking data after payment page redirect' }
    })

    return Promise.resolve({ success: false, response: null })
  }

  // the alias token must never reach the logs - the logger only filters keys literally
  // named "token", so it would pass through untouched
  const paymentForLog = { ...snapshot.body.payment }
  delete paymentForLog.aliasToken

  return dispatch({
    type: BOOK_RESERVATION,
    meta: {
      async: true,
      path: '/reservation',
      method: 'POST',
      body: snapshot.body
    }
  })
    .then(result => {
      if (!result?.success) {
        // failed bookings resolve here (the api middleware never rejects) - the payment was
        // already authorized at SaferPay, so log the replayed request for support to debug
        // the rejection and void the authorization
        logErrorWithJsonData(
          'Payment page booking was rejected',
          {
            status: result?.status,
            response: result?.response,
            message: result?.message,
            payment: paymentForLog,
            rooms: snapshot.body.rooms,
            referenceCode: snapshot.body.referenceCode,
            request: snapshot.request
          },
          { critical: 'true', description: 'POST /reservation failed after an authorized payment page transaction' }
        )
      }

      // restore the basket and request so the reservation summary, GTM tracking and
      // confirmation screen work with the data that was actually booked
      dispatch({ type: SET_REQUEST, payload: snapshot.request })
      dispatch(setBasket(snapshot.basket))

      return { ...result, guestDetails: snapshot.body.guest }
    })
    .catch(error => {
      // error fields are extracted explicitly because they are non-enumerable and would be
      // dropped by the logger's object flattening
      logErrorWithJsonData(
        'Payment page booking failed unexpectedly',
        {
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack
          },
          payment: paymentForLog,
          rooms: snapshot.body.rooms,
          referenceCode: snapshot.body.referenceCode,
          request: snapshot.request
        },
        { critical: 'true', description: 'Unexpected error while booking after the payment page redirect' }
      )

      throw error
    })
}

export const cancelReservation = id => ({
  type: CANCEL_RESERVATION,
  meta: {
    async: true,
    path: `/reservation/${id}`,
    method: 'DELETE',
    body: {}
  }
})

export const fetchReservation = id => ({
  type: FETCH_RESERVATION,
  meta: {
    async: true,
    path: `/reservation/${id}`,
    method: 'GET',
    body: {}
  }
})

export const getMonriClientSecret = (authenticityToken, bookingEngineId, amount, currency, isWSPay) => {
  if (isWSPay) {
    return {
      type: FETCH_MONRI_CLIENT_SECRET,
      meta: {
        async: true,
        path: '/payment/wspay/client-secret',
        method: 'POST',
        body: {
          shopId: authenticityToken,
          bookingEngineId: bookingEngineId,
          amount: amount,
          currency: currency
        }
      }
    }
  } else {
    return {
      type: FETCH_MONRI_CLIENT_SECRET,
      meta: {
        async: true,
        path: '/payment/monri/client-secret',
        method: 'POST',
        body: {
          authenticityToken: authenticityToken,
          bookingEngineId: bookingEngineId,
          amount: amount,
          currency: currency
        }
      }
    }
  }
}

export const getStripeClientSecret = (publishableApiKey, bookingEngineId, amount, currency, guestDetails) => ({
  type: FETCH_STRIPE_CLIENT_SECRET,
  meta: {
    async: true,
    path: '/payment/stripe/client-secret',
    method: 'POST',
    body: {
      publishableApiKey: publishableApiKey,
      bookingEngineId: bookingEngineId,
      amount: amount,
      currency: currency,
      guestDetails: guestDetails
    }
  }
})

export const getM2PayClientSecret = (merchantId, bookingEngineId) => ({
  type: FETCH_M2PAY_CLIENT_SECRET,
  meta: {
    async: true,
    path: '/payment/m2pay/client-secret',
    method: 'POST',
    body: {
      merchantId: merchantId,
      webBookingEngineId: bookingEngineId
    }
  }
})

export const acceptBookingOffer = (offerUuid, bookingId, data) => ({
  type: ACCEPT_BOOKING,
  meta: {
    async: true,
    path: `/offer/${offerUuid}/${bookingId}/accept`,
    method: 'PUT',
    body: data
  }
})

export const updateGuestRegistration = (guests, uuid) => ({
  type: UPDATE_GUEST_REGISTRATION,
  meta: {
    async: true,
    path: `/reservation/check-in/${uuid}/submit`,
    method: 'PUT',
    body: { guests: guests }
  }
})

export const saferpayAliasInsert = payload => ({
  type: SAFERPAY_ALIAS_INSERT,
  meta: {
    async: true,
    path: '/payment/saferpay/alias/insert',
    method: 'POST',
    body: payload
  }
})

export const saferpayPaymentPageInitialize = payload => ({
  type: SAFERPAY_PAYMENT_PAGE_INITIALIZE,
  meta: {
    async: true,
    path: '/payment/saferpay/payment-page/initialize',
    method: 'POST',
    body: payload
  }
})

export const saferpayTransactionInitialize = payload => ({
  type: SAFERPAY_TRANSACTION_INITIALIZE,
  meta: {
    async: true,
    path: '/payment/saferpay/transaction/initialize',
    method: 'POST',
    body: payload
  }
})
